import React from 'react';
import { Box, Breadcrumbs, CircularProgress, IconButton, Link, Tab, Tabs, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { getOffsiteCampaignDetails } from '../../redux/buzzbridge/actions';
import { currentCampaignSelector, gettingCampaignDetailsSelector } from '../../redux/buzzbridge/selectors';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';
import { StatusChip } from './BrandCampaignDetailPage';
import { AppInstallCampaignReport } from './campaignReport/AppInstallCampaignReport';
import { CampaignBoostModal } from './campaignReport/CampaignBoostModal';
import { CampaignReportDevToolbar } from './campaignReport/CampaignReportDevToolbar';
import {
    CampaignReportContentList,
    CampaignReportCreatorsLeaderboard,
    CampaignReportExecutiveSummary,
    CampaignReportPlatformBreakdown,
    CampaignReportPlatformChips,
    CampaignReportWindowChips,
} from './campaignReport/CampaignReportPanels';
import { CampaignReportEmptyState, CampaignReportSyncBanner } from './campaignReport/CampaignReportStates';
import { useCampaignReportPage } from './campaignReport/useCampaignReportPage';

function ReportShell({
    campaignId,
    title,
    status,
    subtitle,
    children,
}: {
    campaignId: string;
    title: string;
    status?: string;
    subtitle?: React.ReactNode;
    children: React.ReactNode;
}) {
    const navigate = useNavigate();
    return (
        <MetaTags title={`Report — ${title} | Brand | Thspian`} description="Campaign performance reporting">
            <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 }, maxWidth: 1280, mx: 'auto', width: '100%' }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={{ mb: 3, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.35)' } }}>
                    <Link
                        component={RouterLink}
                        to={ROUTES.BRAND_CAMPAIGNS}
                        underline="hover"
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff' }}>
                        Campaigns
                    </Link>
                    <Link
                        component={RouterLink}
                        to={ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId)}
                        underline="hover"
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff' }}>
                        {title}
                    </Link>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                        Report
                    </Typography>
                </Breadcrumbs>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 3,
                        flexWrap: 'wrap',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId))}
                            sx={{ color: BRAND_LUXE_PRIMARY }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 20, md: 24 },
                                    color: BRAND_LUXE_PRIMARY,
                                }}>
                                Campaign Report: {title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                {status ? <StatusChip status={status} /> : null}
                                {subtitle}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {children}
            </Box>
        </MetaTags>
    );
}

function SocialCampaignReportBody({ campaignId, campaignTitle }: { campaignId: string; campaignTitle: string }) {
    const report = useCampaignReportPage(campaignId, campaignTitle);

    return (
        <>
            <CampaignReportDevToolbar
                dataMode={report.dataMode}
                onDataModeChange={report.setDataMode}
                previewScenario={report.previewScenario}
                onPreviewScenarioChange={report.setPreviewScenario}
            />

            <Tabs
                value={report.tab}
                onChange={(_, v) => report.setTab(v)}
                sx={{
                    mb: 3,
                    minHeight: 40,
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: 'rgba(255,255,255,0.5)',
                    },
                    '& .Mui-selected': { color: BRAND_LUXE_PRIMARY },
                    '& .MuiTabs-indicator': { bgcolor: BRAND_LUXE_PRIMARY },
                }}>
                <Tab value="dashboard" label="Dashboard" />
                <Tab value="creators" label="Creators" />
            </Tabs>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
                <CampaignReportWindowChips value={report.reportWindow} onChange={report.setReportWindow} />
                {report.tab === 'dashboard' ? (
                    <>
                        <CampaignReportPlatformChips active={report.platform} onToggle={report.togglePlatform} />
                        <TextField
                            size="small"
                            placeholder="Search live URL…"
                            value={report.searchInput}
                            onChange={e => report.setSearchInput(e.target.value)}
                            sx={{
                                maxWidth: 360,
                                '& .MuiOutlinedInput-root': {
                                    color: '#fff',
                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                    '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}66` },
                                },
                            }}
                        />
                    </>
                ) : null}
            </Box>

            <CampaignReportSyncBanner
                syncPct={report.syncPct}
                syncing={report.syncing}
                onRefresh={report.refreshAll}
                onQueueSync={report.queueSync}
            />

            {report.permissionsNote ? (
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        borderLeft: `4px solid ${BRAND_LUXE_PRIMARY}`,
                    }}>
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
                        {report.permissionsNote}
                    </Typography>
                </Box>
            ) : null}

            {report.loadingSummary && !report.summary ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                </Box>
            ) : report.tab === 'dashboard' ? (
                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {!report.hasPublishedPosts ? (
                        <CampaignReportEmptyState onRefresh={report.refreshAll} />
                    ) : (
                        <>
                            {report.summary ? <CampaignReportExecutiveSummary summary={report.summary} /> : null}
                            {report.summary ? <CampaignReportPlatformBreakdown summary={report.summary} /> : null}
                            <CampaignReportContentList
                                items={report.topContent}
                                onBoost={report.openBoost}
                                page={report.contentPage}
                                totalPages={report.contentTotalPages}
                                total={report.content?.pagination?.total ?? report.topContent.length}
                                onPageChange={report.setContentPage}
                                contentSort={report.contentSort}
                                onContentSortChange={report.setContentSort}
                            />
                        </>
                    )}
                </Box>
            ) : (
                <Box sx={{ mt: 4 }}>
                    {(report.creators?.creators?.length ?? 0) === 0 && !report.hasPublishedPosts ? (
                        <CampaignReportEmptyState onRefresh={report.refreshAll} />
                    ) : (
                        <CampaignReportCreatorsLeaderboard
                            creators={report.creators?.creators ?? []}
                            sort={report.creatorSort}
                            onSortChange={report.setCreatorSort}
                            page={report.creatorsPage}
                            totalPages={report.creatorsTotalPages}
                            total={report.creators?.pagination?.total ?? report.creators?.creators?.length ?? 0}
                            onPageChange={report.setCreatorsPage}
                        />
                    )}
                </Box>
            )}

            <CampaignBoostModal
                open={Boolean(report.boostItem)}
                onClose={report.closeBoost}
                campaignId={campaignId}
                item={report.boostItem}
            />
        </>
    );
}

export default function BrandCampaignReportPage() {
    const dispatch = useDispatch();
    const { campaignId } = useParams<{ campaignId: string }>();
    const campaign = useSelector(currentCampaignSelector);
    const loadingCampaign = useSelector(gettingCampaignDetailsSelector);

    React.useEffect(() => {
        if (campaignId) dispatch(getOffsiteCampaignDetails({ campaignId, asBrandHost: true }));
    }, [campaignId, dispatch]);

    if (!campaignId) return null;

    if (loadingCampaign && !campaign) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    const title = campaign?.title ?? 'Campaign';
    const isAppInstall = Boolean(campaign?.app_install?.enabled);

    if (isAppInstall) {
        return (
            <ReportShell
                campaignId={campaignId}
                title={title}
                status={campaign?.status}
                subtitle={
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.45)',
                        }}>
                        App install · CPA
                    </Typography>
                }>
                <AppInstallCampaignReport campaignId={campaignId} />
            </ReportShell>
        );
    }

    return (
        <ReportShell
            campaignId={campaignId}
            title={title}
            status={campaign?.status}
            subtitle={
                <Typography
                    sx={{
                        fontSize: 11,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.45)',
                    }}>
                    Live social metrics
                </Typography>
            }>
            <SocialCampaignReportBody campaignId={campaignId} campaignTitle={title} />
        </ReportShell>
    );
}
